import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, Calendar, Clock, MapPin, ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

const DIAS_LABEL = { segunda: 'Segunda-feira', terca: 'Terca-feira', 'terça': 'Terca-feira', quarta: 'Quarta-feira', quinta: 'Quinta-feira', sexta: 'Sexta-feira', sabado: 'Sabado', 'sábado': 'Sabado' };
const DIAS_ORDER = ['segunda', 'terca', 'terça', 'quarta', 'quinta', 'sexta'];

const COLOR_MAP = {
  'Karatê': { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', dot: 'bg-red-500' },
  'Pilates': { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', dot: 'bg-purple-500' },
  'Dança/Ballet': { bg: 'bg-pink-50', border: 'border-pink-200', text: 'text-pink-700', dot: 'bg-pink-500' },
  'Natação': { bg: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-700', dot: 'bg-cyan-500' },
};
const getColors = (mod) => COLOR_MAP[mod] || { bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700', dot: 'bg-indigo-500' };

export default function AulasExtrasFamilia({ selectedFilho, filhoAtual }) {
  const [aulas, setAulas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [studentClass, setStudentClass] = useState('');
  const [expandedDays, setExpandedDays] = useState({});

  useEffect(() => {
    if (selectedFilho) loadAulas();
  }, [selectedFilho]);

  const loadAulas = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('familyToken');
      const res = await fetch(`${API_URL}/api/dnaconnect/aulas-extras/semana?token=${token}&student_id=${selectedFilho}`);
      if (res.ok) {
        const d = await res.json();
        setAulas(d.aulas || []);
        setStudentClass(d.student_class || '');
        const days = {};
        (d.aulas || []).forEach(a => { days[a.weekday] = true; });
        setExpandedDays(days);
      } else {
        setAulas([]);
      }
    } catch {
      setAulas([]);
    }
    finally { setLoading(false); }
  };

  const toggleDay = (day) => setExpandedDays(p => ({ ...p, [day]: !p[day] }));

  // Group by weekday
  const byDay = {};
  aulas.forEach(a => {
    const key = a.weekday;
    if (!byDay[key]) byDay[key] = [];
    byDay[key].push(a);
  });
  const sortedDays = DIAS_ORDER.filter(d => byDay[d]?.length > 0);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500 mb-3" />
        <p className="text-sm text-slate-500">Carregando aulas extras...</p>
      </div>
    );
  }

  if (aulas.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 bg-slate-50 rounded-2xl border border-slate-100">
        <Calendar className="w-14 h-14 text-slate-300 mb-3" />
        <h3 className="text-base font-semibold text-slate-700 mb-1">Sem aulas extras</h3>
        <p className="text-sm text-slate-500 text-center max-w-sm">Nao ha aulas extras vinculadas a {filhoAtual?.name || 'este aluno'} ({studentClass}) nesta semana.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="aulas-extras-familia">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-purple-600" />
          <h3 className="font-semibold text-slate-800">Aulas Extras da Semana</h3>
        </div>
        <Badge variant="outline" className="text-xs">{aulas.length} aula{aulas.length !== 1 ? 's' : ''}</Badge>
      </div>

      {/* Desktop: grid de colunas por dia */}
      <div className="hidden md:grid gap-3" style={{ gridTemplateColumns: `repeat(${sortedDays.length}, minmax(0, 1fr))` }}>
        {sortedDays.map(day => (
          <div key={day} className="space-y-2">
            <div className="bg-purple-600 text-white rounded-xl px-3 py-2">
              <p className="font-semibold text-sm">{DIAS_LABEL[day] || day}</p>
              <p className="text-[10px] text-purple-200">{byDay[day].length} horario{byDay[day].length !== 1 ? 's' : ''}</p>
            </div>
            {byDay[day].map(a => <AulaCard key={a.id} aula={a} />)}
          </div>
        ))}
      </div>

      {/* Mobile: accordion por dia */}
      <div className="md:hidden space-y-2">
        {sortedDays.map(day => (
          <div key={day} className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
            <button onClick={() => toggleDay(day)} className="w-full flex items-center justify-between p-3 bg-purple-50">
              <div>
                <p className="font-semibold text-sm text-purple-800">{DIAS_LABEL[day] || day}</p>
                <p className="text-[10px] text-purple-500">{byDay[day].length} aula{byDay[day].length !== 1 ? 's' : ''}</p>
              </div>
              {expandedDays[day] ? <ChevronUp className="w-4 h-4 text-purple-400" /> : <ChevronDown className="w-4 h-4 text-purple-400" />}
            </button>
            {expandedDays[day] && (
              <div className="p-2 space-y-2">
                {byDay[day].map(a => <AulaCard key={a.id} aula={a} />)}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function AulaCard({ aula }) {
  const c = getColors(aula.modality);
  return (
    <div className={`${c.bg} ${c.border} border rounded-xl p-3 space-y-1.5`}>
      <div className="flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full ${c.dot}`} />
        <p className={`text-sm font-semibold ${c.text}`}>{aula.modality}</p>
      </div>
      <div className="flex items-center gap-1.5 text-xs text-slate-600">
        <Clock className="w-3 h-3" />
        <span>{aula.start_time} - {aula.end_time}</span>
      </div>
      {aula.local && (
        <div className="flex items-center gap-1.5 text-xs text-slate-500">
          <MapPin className="w-3 h-3" />
          <span>{aula.local}</span>
        </div>
      )}
      {aula.is_contraturno && <Badge variant="outline" className="text-[9px] text-amber-600 border-amber-200">Contraturno</Badge>}
    </div>
  );
}
